import { archetypeForDay, archetypeForSeed, dealArchetype } from './archetypes';
import { GRID_SIZE } from './constants';
import { LATEST_GENERATION_EPOCH, generationEpochFor } from './epochs';
import { key } from './geometry';
import { mstFloor, mwFor, statsFor } from './scoring';
import type { Archetype, GenerationEpoch, Point, Puzzle, ScoredPuzzle } from './types';

/** How many seeded attempts before we fall back to open plains. */
const MAX_ATTEMPTS = 60;

/**
 * The deception band (GDD §5), in MW of **headroom**: how much the obvious
 * line leaves on the table.
 *
 * Band the headroom, never the absolute read. A day whose seed line scores 40
 * MW is not a bad day, it is usually the best day of the month — the distance
 * between the obvious line and the good one is where the game lives, and a
 * filter on the naive score throws exactly those days away first. Measured
 * over a year of the river archetype, banding the naive read at 78-90 would
 * have re-rolled three days in four and taken the richest with it.
 *
 * So there is a floor and no ceiling: a day has to leave something to find,
 * and there is no such thing as leaving too much.
 *
 * The floor is a guarantee rather than a guess, because the probe cannot
 * overestimate: it descends to a layout the reference search is by definition
 * at least as good as, so real headroom is never less than probed headroom.
 * Measured over 365 unfiltered days, probe/true never exceeded 1.
 *
 * Four, from that same measurement. It costs 17% of deals and leaves every
 * shipped day at least 4 MW to find. Higher floors were rejected for what
 * they quietly select *for*: at 10, a quarter of the days thrown out have
 * more than 10 MW of real headroom that this greedy probe simply failed to
 * see — which are the deep ones, the maps whose gains do not come from
 * walking downhill. A floor that filters for "easy to find greedily" would
 * cost exactly the days worth keeping.
 */
const HEADROOM_FLOOR = 4;

/** How far the probe descends before it calls the headroom measured. */
const PROBE_STEPS = 8;

/**
 * Open plains with a fixed solvable layout. Only reached if 60 seeded attempts
 * all fail, which has not happened in testing — but the game must never be
 * unable to hand the player a grid.
 *
 * Plains have no barrier at all, so the honest tag is radial, whatever shape
 * the day was reaching for.
 */
export function fallbackPuzzle(archetype: Archetype = 'radial'): Puzzle {
  return {
    plant: { r: 4, c: 0 },
    houses: [
      { r: 2, c: 7 },
      { r: 6, c: 7 },
      { r: 4, c: 8 },
    ],
    obstacles: {},
    par: 4,
    seedTowers: [
      { r: 4, c: 2 },
      { r: 4, c: 4 },
      { r: 4, c: 6 },
      { r: 3, c: 7 },
    ],
    archetype,
  };
}

/** Perturb a seed into the mix for one attempt. The epoch's stream depends on this. */
const mixFor = (seed: number, attempt: number): number =>
  ((seed ^ 0x9e3779b9) + Math.imul(attempt, 0x85ebca6b)) | 0;

/**
 * The naive read: what the generator's own line delivers, measured against the
 * same wire floor the player's score uses.
 *
 * Only scoring helpers are involved, never the solver. Generation that leaned
 * on search output would let a better search silently change which map a day
 * deals (GDD §5, §8), and a moved map is a different game.
 */
function naiveRead(puzzle: Puzzle, wire: number): number {
  return mwFor({ ...puzzle, mst: mstFloor(puzzle) } as ScoredPuzzle, wire);
}

/** Every cell a tower may stand on, whatever is standing there now. */
function openCells(puzzle: Puzzle): Point[] {
  const blocked = new Set([
    key(puzzle.plant),
    ...puzzle.houses.map(key),
    ...Object.keys(puzzle.obstacles),
  ]);
  const cells: Point[] = [];
  for (let r = 0; r < GRID_SIZE; r++) {
    for (let c = 0; c < GRID_SIZE; c++) {
      if (!blocked.has(r + ',' + c)) cells.push({ r, c });
    }
  }
  return cells;
}

/**
 * How much better than its own seed line a map can be made, in MW.
 *
 * A short steepest descent — at each step, the single best of "drop a tower"
 * or "move a tower somewhere else", stopping when nothing improves or after
 * {@link PROBE_STEPS}. It is a probe, not a search: it will not find what the
 * reference finds, and it does not need to. It only has to tell a map with
 * something in it from a map without.
 *
 * This is the generator's own, frozen in the epoch that runs it, and it never
 * calls the solver. Generation that leaned on search output would let a better
 * search silently change which map a day deals (GDD §5, §8), and a moved map
 * is a different game.
 */
export function probeHeadroom(puzzle: Puzzle): number {
  const seed = statsFor(puzzle, puzzle.seedTowers);
  if (!seed.ok) return 0;

  const cells = openCells(puzzle);
  let cur = puzzle.seedTowers;
  let wire = seed.wire;

  for (let step = 0; step < PROBE_STEPS; step++) {
    let bestTowers: Point[] | null = null;
    let bestWire = wire - 1e-9;

    for (let i = 0; i < cur.length; i++) {
      const rest = cur.filter((_, j) => j !== i);
      // Dropping a tower outright is a tuning move like any other, and often
      // the one the reference makes: under par is the good ending.
      const dropped = statsFor(puzzle, rest);
      if (dropped.ok && dropped.wire < bestWire) {
        bestWire = dropped.wire;
        bestTowers = rest;
      }
      for (const cell of cells) {
        if (cur.some((t) => key(t) === key(cell))) continue;
        const moved = rest.concat([cell]);
        const s = statsFor(puzzle, moved);
        if (s.ok && s.wire < bestWire) {
          bestWire = s.wire;
          bestTowers = moved;
        }
      }
    }

    if (!bestTowers) break;
    cur = bestTowers;
    wire = bestWire;
  }

  return naiveRead(puzzle, wire) - naiveRead(puzzle, seed.wire);
}

/**
 * How far a candidate falls short of the deception band — 0 once it leaves
 * enough on the table to be worth tuning.
 */
function bandMiss(puzzle: Puzzle): number {
  return Math.max(0, HEADROOM_FLOOR - probeHeadroom(puzzle));
}

/**
 * Pick the shape, deal it, and hold it to the flatness floor.
 *
 * When no attempt lands inside the band, the closest near-miss is dealt rather
 * than the open-plains fallback. The game must never fail to deal a grid, and
 * it must never deal a grid of unknown quality while a nearly-good one is in
 * hand.
 */
function dealSeeded(seed: number, archetype: Archetype): Puzzle {
  let best: Puzzle | null = null;
  let bestMiss = Infinity;

  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const puzzle = dealArchetype(archetype, mixFor(seed, attempt));
    if (!puzzle) continue;
    const miss = bandMiss(puzzle);
    if (miss === 0) return puzzle;
    // Strictly better only, so the earliest attempt settles a tie and the day
    // deals the same near-miss on every device.
    if (miss < bestMiss) {
      bestMiss = miss;
      best = puzzle;
    }
  }
  return best ?? fallbackPuzzle(archetype);
}

/**
 * The generators, addressed by epoch.
 *
 * A new epoch registers here beside the old one; none is ever edited in place.
 * The `Record<GenerationEpoch, …>` type is what forces that: adding a version
 * to the union without adding its generator here is a compile error.
 */
const GENERATORS: Record<GenerationEpoch, (seed: number, archetype: Archetype) => Puzzle> = {
  1: dealSeeded,
};

/**
 * The grid for a given seed. Same seed, same grid, everywhere, forever.
 *
 * The epoch defaults to the newest, which is what practice and any other
 * dateless deal want (GDD §4.4). Daily grids go through {@link generateForDay}
 * instead, so a grid number always resolves to the generator that first dealt
 * it — and to the archetype walk, which practice has no place in.
 */
export function generate(seed: number, epoch: GenerationEpoch = LATEST_GENERATION_EPOCH): Puzzle {
  return GENERATORS[epoch](seed, archetypeForSeed(seed));
}

/**
 * The grid for a daily number, dealt by whichever generator owns it.
 *
 * Two things come from the day rather than the seed: the epoch (§5.5's
 * pinning) and the archetype, which is walked forward so today's shape is
 * never yesterday's.
 */
export function generateForDay(day: number): Puzzle {
  return GENERATORS[generationEpochFor(day)](day, archetypeForDay(day));
}
